import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Target,
    Bot,
    Cpu,
    Puzzle,
    ShieldCheck,
    User,
    Link2,
} from "lucide-react";
import { useBookCall } from "@/components/BookCallContext";
import { TESTIDS } from "@/constants/testIds";
import SEO from "@/components/SEO";
import AgenticEngineDiagram from "@/components/AgenticEngineDiagram";

const WHAT = [
    {
        icon: Bot,
        title: "AI Agents",
        body:
            "Domain-aware agents that handle real work across channels and systems.",
    },
    {
        icon: Cpu,
        title: "Agentic Engine",
        body:
            "Our proprietary engine for reasoning, tools, memory and safe execution.",
    },
    {
        icon: Puzzle,
        title: "Enterprise Integrations",
        body:
            "Secure, prebuilt and custom connectors to the systems that run your business.",
    },
];

const HOW = [
    {
        icon: ShieldCheck,
        title: "Built for real workflows",
        body: "We start where work actually happens.",
    },
    {
        icon: User,
        title: "Human-in-the-loop",
        body: "Agents act with oversight so humans stay in control.",
    },
    {
        icon: Link2,
        title: "Grounded in systems",
        body: "We connect to your data and systems for accuracy.",
    },
];

const ACCENT = "#8fd0ff";

export default function About() {
    const { openModal } = useBookCall();

    return (
        <div data-testid={TESTIDS.about.root}>
            <SEO
                path="/about"
                title="About KyroLabs, the engine behind every agent"
                description="KyroLabs builds AI agents for the job, on an engine we own. Learn how our agentic engine turns conversations, workflows, knowledge and systems into enterprise-grade AI."
            />
            {/* ===== HERO ===== */}
            <section style={{ padding: "60px 0 40px" }}>
                <div className="wrap">
                    <div className="about-hero" style={heroGrid}>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                        >
                            <span className="kicker" style={{ color: ACCENT }}>
                                About KyroLabs
                            </span>
                            <h1 className="h1" style={{ margin: "14px 0 18px" }}>
                                We build agents for the job, on an engine we own.
                            </h1>
                            <p
                                style={{
                                    color: "var(--kl-muted)",
                                    fontSize: "1.05rem",
                                    lineHeight: 1.65,
                                    maxWidth: 560,
                                    marginBottom: 28,
                                }}
                            >
                                We help enterprises turn conversations, workflows, knowledge
                                and systems into AI-powered agents that can think, act and
                                improve.
                            </p>
                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                <button
                                    data-testid={TESTIDS.about.bookCall}
                                    onClick={() => openModal({ source: "about" })}
                                    className="btn btn-p"
                                >
                                    Book a Demo <ArrowRight size={14} />
                                </button>
                                <Link
                                    to="/products/kyro-engage"
                                    className="btn"
                                    style={{
                                        border: "1px solid var(--line-strong)",
                                        color: "var(--ink)",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                    }}
                                >
                                    Explore Products <ArrowRight size={14} />
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.55 }}
                            style={diagramWrap}
                            aria-hidden
                        >
                            <AgenticEngineDiagram accent={ACCENT} />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ===== WHY WE EXIST ===== */}
            <section style={{ padding: "20px 0 40px" }}>
                <div className="wrap">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5 }}
                        style={whyCard(ACCENT)}
                    >
                        <div style={whyIconWrap(ACCENT)}>
                            <Target size={44} strokeWidth={1.4} color={ACCENT} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h2
                                className="font-display"
                                style={{
                                    fontSize: "1.6rem",
                                    fontWeight: 600,
                                    marginBottom: 12,
                                }}
                            >
                                Why we exist
                            </h2>
                            <p
                                style={{
                                    color: "var(--kl-muted)",
                                    fontSize: "1rem",
                                    lineHeight: 1.7,
                                    maxWidth: 780,
                                }}
                            >
                                AI is no longer just a chatbot layer. Businesses need agents
                                that understand context, connect to systems, follow workflows
                                and act with human oversight. KyroLabs exists to make that
                                real, reliably, securely and at enterprise scale.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ===== WHAT WE BUILD ===== */}
            <section style={{ padding: "40px 0" }}>
                <div className="wrap">
                    <h2 className="section-h2" style={{ marginBottom: 22 }}>
                        What we build
                    </h2>
                    <div className="about-grid-3" style={grid3}>
                        {WHAT.map((c, i) => (
                            <FeatureCard
                                key={c.title}
                                icon={c.icon}
                                title={c.title}
                                body={c.body}
                                delay={i * 0.08}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== HOW WE WORK ===== */}
            <section style={{ padding: "20px 0 40px" }}>
                <div className="wrap">
                    <h2 className="section-h2" style={{ marginBottom: 22 }}>
                        How we work
                    </h2>
                    <div className="about-grid-3" style={grid3}>
                        {HOW.map((c, i) => (
                            <FeatureCard
                                key={c.title}
                                icon={c.icon}
                                title={c.title}
                                body={c.body}
                                delay={i * 0.08}
                                variant="row"
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== FINAL CTA BAND ===== */}
            <section style={{ padding: "20px 0 90px" }}>
                <div className="wrap">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5 }}
                        style={ctaBand(ACCENT)}
                        className="about-cta"
                    >
                        <h3
                            className="font-display"
                            style={{
                                fontSize: "1.55rem",
                                fontWeight: 600,
                                color: "#fff",
                                margin: 0,
                            }}
                        >
                            Build your first AI agent with KyroLabs
                        </h3>
                        <button
                            data-testid={TESTIDS.about.bookCallCta}
                            onClick={() => openModal({ source: "about-cta" })}
                            className="btn btn-p"
                            style={{ flexShrink: 0 }}
                        >
                            Book a Demo <ArrowRight size={14} />
                        </button>
                    </motion.div>
                </div>
            </section>

            <style>{`
                .section-h2 {
                    font-family: var(--font-display);
                    font-size: 1.7rem;
                    font-weight: 600;
                    color: var(--ink);
                    letter-spacing: -0.01em;
                }
                @media (max-width: 900px) {
                    .about-hero { grid-template-columns: 1fr !important; gap: 32px !important; }
                    .about-grid-3 { grid-template-columns: 1fr !important; }
                    .about-cta { flex-direction: column !important; align-items: flex-start !important; gap: 18px !important; }
                }
            `}</style>
        </div>
    );
}

function FeatureCard({ icon: Icon, title, body, delay = 0, variant = "col" }) {
    const isRow = variant === "row";
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay }}
            style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid var(--line)",
                borderRadius: 16,
                padding: isRow ? "22px 26px" : "26px 28px",
                display: "flex",
                flexDirection: isRow ? "row" : "column",
                alignItems: isRow ? "center" : "flex-start",
                gap: isRow ? 18 : 20,
                minHeight: isRow ? 120 : 200,
            }}
        >
            <div style={iconTile(ACCENT)}>
                <Icon size={28} strokeWidth={1.5} color={ACCENT} />
            </div>
            <div style={{ minWidth: 0 }}>
                <h3
                    className="font-display"
                    style={{
                        fontSize: "1.15rem",
                        fontWeight: 600,
                        color: "var(--ink)",
                        marginBottom: 8,
                    }}
                >
                    {title}
                </h3>
                <p style={{ color: "var(--kl-muted)", fontSize: ".96rem", lineHeight: 1.6 }}>
                    {body}
                </p>
            </div>
        </motion.div>
    );
}

/* ---------- styles ---------- */

const heroGrid = {
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr",
    gap: 48,
    alignItems: "center",
};
const diagramWrap = {
    borderRadius: 18,
    border: "1px solid var(--line)",
    background: "radial-gradient(80% 60% at 40% 20%, rgba(143,208,255,0.08), transparent 60%),#0a0d12",
    padding: 24,
    minHeight: 400,
    display: "grid",
    placeItems: "center",
};

const whyCard = (accent) => ({
    display: "flex",
    alignItems: "center",
    gap: 32,
    padding: "32px 36px",
    borderRadius: 20,
    border: `1px solid ${accent}22`,
    background: `radial-gradient(80% 100% at 0% 50%, ${accent}0f, transparent 60%),rgba(255,255,255,0.02)`,
});
const whyIconWrap = (accent) => ({
    width: 116,
    height: 116,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    background: `radial-gradient(circle at 40% 40%, ${accent}20, transparent 65%)`,
    border: `1px solid ${accent}33`,
});
const iconTile = (accent) => ({
    width: 52,
    height: 52,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: `radial-gradient(60% 60% at 40% 40%, ${accent}22, ${accent}05 70%)`,
    border: `1px solid ${accent}33`,
    flexShrink: 0,
});

const grid3 = {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
};

const ctaBand = (accent) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    padding: "28px 36px",
    borderRadius: 18,
    background: `linear-gradient(90deg, ${accent}18 0%, transparent 70%),rgba(255,255,255,0.02)`,
    border: `1px solid ${accent}33`,
    boxShadow: `0 30px 80px ${accent}12`,
});
